import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './hooks/useAuth';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import { trackPageView } from './utils/analytics';
import ThemeProvider from './theme/ThemeProvider';
import PublicLayout from './components/layouts/PublicLayout';

// Public Pages
import Home from './pages/public/Home';
import About from './pages/public/About';
import Solutions from './pages/public/Solutions';
import Contact from './pages/public/Contact';
import Events from './pages/public/Events';
import Blogs from './pages/public/Blogs';
import Subscriptions from './pages/public/Subscriptions';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';
import PublicShop from './pages/public/PublicShop';
import FindExpert from './pages/public/FindExpert';
import BootcampRegistration from './pages/public/BootcampRegistration';
import Trainings from './pages/public/solutions/Trainings';
import TechIntegration from './pages/public/solutions/TechIntegration';
import ParentalEngagement from './pages/public/solutions/ParentalEngagement';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import SchoolRegister from './pages/auth/SchoolRegister';
import AdminRegister from './pages/auth/AdminRegister';
import TeacherRegister from './pages/auth/TeacherRegister';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Admin Pages
import AdminPanel from './pages/admin/AdminPanel';

// School Pages
import SchoolPanel from './pages/schools/SchoolPanel';

// Teacher Pages
import TeacherPanel from './pages/teachers/TeacherPanel';

// Parent Pages
import ParentPanel from './pages/parents/ParentPanel';

// Mentor Pages
import MentorPanel from './pages/mentors/MentorPanel';

// Dashboard Pages
import Checkout from './pages/dashboard/Checkout';

// Not Found Page
import NotFound from './pages/public/NotFound';

// RouteTracker component to handle route change analytics
const RouteTracker = () => {
  const location = useLocation();
  
  React.useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  
  return null;
};

// Wrapper component for public routes
const PublicRoute = ({ element }) => {
  return (
    <PublicLayout>
      {element}
    </PublicLayout>
  );
};

// RouteTrackerWrapper needs to be inside the Router
const AppRoutes = () => {
  return (
    <>
      <RouteTracker />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute element={<Home />} />} />
        <Route path="/about" element={<PublicRoute element={<About />} />} />
        <Route path="#" element={<PublicRoute element={<Solutions />} />} />
  <Route path="/solutions/tech-integration" element={<PublicRoute element={<TechIntegration />} />} />
  <Route path="/solutions/trainings" element={<PublicRoute element={<Trainings />} />} />
  <Route path="/solutions/parental-engagement" element={<PublicRoute element={<ParentalEngagement />} />} />
        <Route path="/contact" element={<PublicRoute element={<Contact />} />} />
        <Route path="/events" element={<PublicRoute element={<Events />} />} />
        <Route path="/blogs" element={<PublicRoute element={<Blogs />} />} />
        <Route path="/subscriptions" element={<PublicRoute element={<Subscriptions />} />} />
        <Route path="/privacy-policy" element={<PublicRoute element={<PrivacyPolicy />} />} />
        <Route path="/terms-of-service" element={<PublicRoute element={<TermsOfService />} />} />
        <Route path="/shop/:shopId" element={<PublicRoute element={<PublicShop />} />} />
        <Route path="/find-expert" element={<PublicRoute element={<FindExpert />} />} />
        <Route path="/bootcamp/register" element={<PublicRoute element={<BootcampRegistration />} />} />
        <Route path="/resources" element={<PublicRoute element={<NotFound />} />} />
        <Route path="/faqs" element={<PublicRoute element={<NotFound />} />} />
        <Route path="/sitemap" element={<PublicRoute element={<NotFound />} />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<PublicRoute element={<Login />} />} />
        <Route path="/register" element={<PublicRoute element={<Register />} />} />
        <Route path="/register/school" element={<PublicRoute element={<SchoolRegister />} />} />
        <Route path="/register/admin" element={<PublicRoute element={<AdminRegister />} />} />
        <Route path="/register/teacher" element={<PublicRoute element={<TeacherRegister />} />} />
        <Route path="/forgot-password" element={<PublicRoute element={<ForgotPassword />} />} />
        <Route path="/reset-password/:resetToken" element={<PublicRoute element={<ResetPassword />} />} />
        
        {/* Checkout Route */}
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute roles={['system_admin']}>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/school/*"
          element={
            <ProtectedRoute roles={['system_admin', 'school_admin']}>
              <SchoolPanel />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/teacher/*"
          element={
            <ProtectedRoute roles={['teacher']}>
              <TeacherPanel />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/parent/*"
          element={
            <ProtectedRoute roles={['parent']}>
              <ParentPanel />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/mentor/*"
          element={
            <ProtectedRoute roles={['mentor']}>
              <MentorPanel />
            </ProtectedRoute>
          }
        />
        
        {/* 404 Route */}
        <Route path="*" element={<PublicRoute element={<NotFound />} />} />
      </Routes>
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <ThemeProvider>
          <Router>
            <ToastContainer position="top-right" autoClose={3000} />
            <AppRoutes />
          </Router>
        </ThemeProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;